<template>
  <v-row
    class="connector-card flex-column pa-5 pb-4 mx-2"
    justify="space-between"
    no-gutters
    :data-test-id="`${connectorSourceId}-connector-card`"
  >
    <div>
      <v-row no-gutters justify="space-between" class="mb-3">
        <!-- Connector Image -->
        <div class="connector-card__image">
          <img
            class="w-100 h-100"
            :src="getConnectorLogoPath"
            :alt="getConnectorName"
          />
        </div>
        <div>
          <!-- Connector Status label -->
          <v-chip
            v-if="hasConsent"
            label
            x-small
            class="font500"
            :color="getConnectorStatus('color')"
          >
            <span>{{ getConnectorStatus() }}</span>
          </v-chip>
          <!-- Connector More Options -->
          <span v-if="isConnectorJobCompleted" class="text-center ml-1">
            <v-menu offset-y>
              <template v-slot:activator="{ on: onMenu }">
                <!-- Tooltip -->
                <v-tooltip top max-width="200px">
                  <template v-slot:activator="{ on: onTooltip }">
                    <v-icon
                      dense
                      v-on="{ ...onMenu, ...onTooltip }"
                      :data-test-id="
                        `${connectorSourceId}-connector-card__action-icon`
                      "
                    >
                      mdi-dots-vertical
                    </v-icon>
                  </template>
                  <span class="tooltip">Actions</span>
                </v-tooltip>
              </template>
              <!-- Connector More Action List -->
              <v-list
                :data-test-id="
                  `${connectorSourceId}-connector-card__action-list`
                "
              >
                <v-list-item
                  class="px-3"
                  v-for="(option, index) in connectorOptions"
                  :key="index"
                  @click="() => connectorConfig(option)"
                >
                  <v-list-item-title class="font14">
                    {{ option }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </span>
        </div>
      </v-row>
      <!-- Connector Name -->
      <h4 class="font600 mb-2 ">{{ getConnectorName }}</h4>
      <!-- Connector Description -->
      <p class="font300 font12 mb-0">
        {{ getConnectorDescription }}
      </p>
    </div>
    <!-- Connector Connect/Disconnect Button -->
    <v-tooltip
      bottom
      max-width="300px"
      v-if="!hasConsent && !isDisabled && isDynamicConnector"
    >
      <template v-slot:activator="{ on, attrs }">
        <div class="w-100" v-on="on" v-bind="attrs">
          <x-button
            class="mt-6"
            style="width:100% !important"
            :outline="hasConsent"
            :square="false"
            :color="colors && colors.primary"
            @click="buttonAction"
            :disabled="isDisabled"
            :buttonText="buttonText"
            :data-test-id="`${connectorSourceId}-connector-card__btn`"
          />
        </div>
      </template>
      <span class="tooltip text-center font10 mb-1">
        {{ this.$message.CONNECTOR_MESSAGE.NOT_VERIFIED }}
      </span>
    </v-tooltip>
    <x-button
      v-else
      class="mt-6"
      style="width:100% !important"
      :outline="hasConsent"
      :square="false"
      :color="colors && colors.primary"
      @click="buttonAction"
      :disabled="isDisabled"
      :buttonText="buttonText"
      :data-test-id="`${connectorSourceId}-connector-card__btn`"
    />
  </v-row>
</template>
<script>
import { mapState } from 'vuex';
import { connectorsConfig } from '@/components/Organisations/Overview/SSO/connectorsConfig';
export default {
  name: 'Connector',
  props: {
    subscriptionDetails: Object,
    sourceDetails: Object,
    showButtons: { type: Boolean, default: true },
    index: Number,
    connectorId: String,
    showToggleButton: { type: Boolean, default: false },
    dataTestId: String
  },
  data() {
    return {
      connectorStatusLabels: {
        connected: 'Connected',
        inProgress: 'Syncing...'
      },
      connectorActionLabels: ['Sync Now', 'Re-Authorize'],
      vaultConnectorActionLabels: ['Integration Settings', 'Re-Authorize'],
      exportConnectorActionLabels: ['Re-Authorize'],
      connectorsConfig
    };
  },
  computed: {
    ...mapState(['onboardingStatus']),
    colors() {
      return this.$vuetify?.theme?.themes?.light;
    },
    isDisabled() {
      const isDisabled =
        this.subscriptionDetails?.HAS_CONSENT &&
        !this.subscriptionDetails?.ONBOARD_COMPLETE;
      if (this.showToggleButton) {
        return isDisabled;
      }
      return !this.isOrgOnboarded || isDisabled;
    },
    isOrgOnboarded() {
      return this.onboardingStatus === 'ONBOARDED';
    },
    connectorOptions() {
      // other connectors are those which don't sync any data like:- SSO and eDiscovery export connectors
      const otherConnectors = [
        this.$preference.SINGLE_SIGN_ON,
        ...this.$enums.exportableConnectors
      ];
      if (otherConnectors.includes(this.sourceDetails.SOURCE_ID)) {
        return this.exportConnectorActionLabels;
      }
      return this.isDynamicConnector
        ? this.vaultConnectorActionLabels
        : this.connectorActionLabels;
    },
    getConnectorLogoPath() {
      return this.$images.CONNECTOR_LOGO(this.sourceDetails?.SOURCE_ID);
    },
    isConnectorJobCompleted() {
      return this.showToggleButton
        ? false
        : this.subscriptionDetails?.ONBOARD_COMPLETE;
    },
    /**
     * @param {String} get
     * returns connector label text & color
     */
    getConnectorStatus() {
      return (get = 'text') => {
        let connectorStatusLabel = this.connectorStatusLabels.inProgress;
        let labelColor = this.colors?.inProgress;
        if (this.subscriptionDetails?.ONBOARD_COMPLETE) {
          connectorStatusLabel = this.connectorStatusLabels.connected;
          labelColor = this.colors?.primaryShade1;
        }
        return get === 'color' ? labelColor : connectorStatusLabel;
      };
    },
    getConnectorName() {
      return this.sourceDetails?.SOURCE_NAME || '';
    },
    connectorSourceId() {
      return this.sourceDetails?.SOURCE_ID || '';
    },
    hasConsent() {
      return this.showButtons && this.subscriptionDetails?.HAS_CONSENT;
    },
    buttonText() {
      return this.hasConsent ? 'Disconnect' : 'Connect';
    },
    buttonAction() {
      return this.hasConsent
        ? this.disconnectConnector
        : () =>
            this.emitMethod(
              'authoriseOfficeConnector',
              this.sourceDetails,
              this.connectorId
            );
    },
    getConnectorDescription() {
      return (
        this.connectorsConfig?.[this.sourceDetails?.SOURCE_ID]?.description ||
        ''
      );
    },
    isDynamicConnector() {
      return this.$enums.dynamicConnectors.includes(
        this.sourceDetails.SOURCE_ID
      );
    }
  },
  methods: {
    /**
     * To performe connector actions
     * @param {String} action
     */
    connectorConfig(action) {
      switch (action) {
        case 'Sync Now':
          this.syncNow(this.sourceDetails.SOURCE_ID);
          break;
        case 'Re-Authorize':
          this.emitMethod(
            'authoriseOfficeConnector',
            this.sourceDetails,
            this.connectorId
          );
          break;
        default:
          this.$emit('openIntegrationDialog', true, {
            subscriptionDetails: this.subscriptionDetails,
            sourceDetails: this.sourceDetails,
            connectorId: this.connectorId
          });
          break;
      }
    },
    /**
     * To sync connector data
     * @param {String} consumerType
     */
    async syncNow(consumerType) {
      try {
        const syncData = await this.$services.sync.syncNow({ consumerType });
        if (syncData) {
          this.snackbar(
            'success',
            this.$message?.CONNECTOR_MESSAGE?.SYNC_STARTED(
              this.getConnectorName
            )
          );
        }
      } catch (err) {
        this.snackbar('error', err);
      }
    },
    /**
     * To disconnect connector syncing
     */
    disconnectConnector() {
      const { SINGLE_SIGN_ON, SINGLE_SIGN_ON_STATUS } = this.$preference;
      const sourceId = this.sourceDetails?.SOURCE_ID;
      const activeDialogue =
        sourceId === SINGLE_SIGN_ON ? SINGLE_SIGN_ON_STATUS : sourceId;
      const dialogData = {
        activeDialogue,
        index: this.index,
        id: this.connectorId,
        name: this.sourceDetails?.SOURCE_NAME
      };
      this.$emit('openDialog', true, dialogData);
    },
    emitMethod(type, ...rest) {
      this.$emit(type, ...rest);
    }
  }
};
</script>
<style scoped>
.connector-card {
  border: 1px solid #e5e5e5;
  height: 100%;
}
.connector-card > button {
  width: 100% !important;
}
.connector-card__image {
  width: 44px;
  height: 44px;
}
.v-tooltip__content {
  color: #fff !important;
  background: #1b2342 !important;
  border-color: #1b2342 !important;
  padding: 4px 8px !important;
}
.tooltip {
  color: #fff !important;
  text-align: center;
  font-size: 10px;
  font-weight: normal;
  line-height: 16px;
  margin-bottom: 4px;
}
</style>
